import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useMemo, useState } from 'react';
import { useTheme } from '@material-ui/core/styles';
import { useForm, useFieldArray } from 'react-hook-form';
import useQueryString from 'shared/hooks/useQueryString';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import PropTypes from 'prop-types';
import {
  Box, Button, Popper, Fade, Paper, ClickAwayListener,
} from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import CustomButtonSearch from 'apps/searchPublic/components/CustomButtonSearch';
import SearchSelectValue from 'apps/searchPublic/components/SearchSelectValue';
import LocationFilter from 'shared/components/BlockButtonsSearch/partials/LocationsPopper/partials/LocationFilter';
import CompactStates from 'shared/components/BlockButtonsSearch/partials/CompactStates';
import clsx from 'clsx';
import LocationIcon from '@mui/icons-material/LocationOn';
import locationsSchema from './schema';
import locationsPopperStyles from './styles';

const DEFAULT_LOCATION = { location: null, radius: '50' };

const LocationsPopper = ({
  defaults, defaultsReady, setQueryStringValue, showSubmitButton, buttonAsAutocomplete, setPreventSearch,
  handleResetFilters, iconInMobile,
  noSubmit, error, setError,
}) => {
  const classes = locationsPopperStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { urlParams, setQueryString } = useQueryString();

  const defaultsJSON = JSON.parse(defaults || '{}');
  const defaultLocations = defaultsJSON.locations?.length ? defaultsJSON.locations : [DEFAULT_LOCATION];
  const defaultStates = defaultsJSON.compactStates || urlParams.compactStates || [];

  const [anchorEl, setAnchorEl] = useState(null);
  const [disabled, setDisabled] = useState(false);
  const [compactStates, setCompactStates] = useState(defaultStates);

  const {
    control, handleSubmit, watch, setValue, formState, trigger,
  } = useForm({
    defaultValues: { locations: defaultLocations },
    resolver: yupResolver(locationsSchema),
    mode: 'onChange',
  });

  useEffect(() => {
    setPreventSearch(Object.keys(formState.errors).length !== 0);
  }, [formState, setPreventSearch]);

  const { fields, append, remove } = useFieldArray({
    control, name: 'locations',
  });
  const locationFields = watch('locations').map((l, i) => ({ ...l, id: fields[i]?.id }));

  useEffect(() => {
    const locationsParam = urlParams.location || [];
    const locationsList = Array.isArray(locationsParam) ? locationsParam : [locationsParam];
    const locations = locationsList.map(l => {
      const [placeId, radius, description, id] = l.split(/_radius_|_description_|_id_/);
      return { location: { description: description.replaceAll('_', ','), place_id: placeId }, radius, id };
    });
    if (!defaultsReady && !locations.length && locationFields[0].location && defaultLocations[0].location) {
      const location = locationFields.map((l, i) => (
        `${l.location.place_id}_radius_${l.radius}_description_${l.location.description.replaceAll(',', '_')}_id_${fields[i].id}`
      ));
      setQueryString({ ...urlParams, location });
    } else {
      const loc = locations.length ? locations : [DEFAULT_LOCATION];
      setValue('locations', loc);
      if (setQueryStringValue && locations.length) {
        setQueryStringValue(prevState => ({
          ...prevState,
          location: locations.map(l => (
            `${l.location.place_id}_radius_${l.radius}_description_${l.location.description.replaceAll(',', '_')}_id_${l.id}`
          )),
        }));
      }
    }

    const statesParam = urlParams.compactStates || [];
    const statesList = Array.isArray(statesParam) ? statesParam : [statesParam];
    if (!defaultsReady && !statesList.length && compactStates.length && defaultStates.length) {
      setQueryString({ ...urlParams, compactStates });
    } else {
      setCompactStates(statesList);
      if (setQueryStringValue && statesList) {
        setQueryStringValue(prevState => ({ ...prevState, compactStates: statesList }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlParams, setQueryStringValue]);

  const disableSubmitButton = useMemo(() => {
    if (!showSubmitButton) return true;
    return locationFields.some(
      l => !l.location?.types?.includes('administrative_area_level_1')
        && (Number.isNaN(parseInt(l.radius, 10)) || parseInt(l.radius, 10) < 25),
    );
  }, [locationFields, showSubmitButton]);

  const handleClick = event => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const applyFilters = () => {
    const locations = watch('locations');
    setQueryStringValue(previousData => {
      const previousQueryStringData = { ...previousData };
      const locationsData = locations ? locations.filter(l => l.location).map((l, i) => {
        if (l.location.types && l.location.types.includes('administrative_area_level_1')) {
          return `${l.location.place_id}_radius_0_description_${l.location.description.replaceAll(',', '_')}_id_${fields[i].id}`;
        }
        return `${l.location.place_id}_radius_${l.radius}_description_${l.location.description.replaceAll(',', '_')}_id_${fields[i].id}`;
      }) : [];
      delete previousQueryStringData.first;
      if (setError) setError('location', null);
      return { ...previousQueryStringData, location: locationsData };
    });
  };

  const handleClose = () => {
    setValue('locations', locationFields.filter((l, index) => index === 0 || l.location));
    if (setQueryStringValue) {
      applyFilters();
    }
    setAnchorEl(null);
    setDisabled(false);
  };

  useEffect(() => {
    if (!setQueryStringValue) return;
    if (setError) setError('location', null);
    setQueryStringValue(previousData => ({ ...previousData, compactStates }));
  }, [compactStates, setQueryStringValue, setError]);

  const onSubmit = data => {
    setDisabled(true);
    const locationsData = data.locations ? data.locations.map((l, i) => {
      if (l.location.types && l.location.types.includes('administrative_area_level_1')) {
        return `${l.location.place_id}_radius_0_description_${l.location.description.replaceAll(',', '_')}_id_${fields[i].id}`;
      }
      return `${l.location.place_id}_radius_${l.radius}_description_${l.location.description.replaceAll(',', '_')}_id_${fields[i].id}`;
    }) : [];
    delete urlParams.first;
    setQueryString({ ...urlParams, location: locationsData, compactStates });
    handleClose();
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popper' : undefined;

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <Box className={!buttonAsAutocomplete ? classes.box : null} width={isMobile && !buttonAsAutocomplete ? '50%' : 'auto'}>
        <CustomButtonSearch
          variant="outlined"
          className={clsx(
            { [classes.buttonError]: error },
            { [classes.popperOpen]: open && !buttonAsAutocomplete },
            { [classes.popperOpenButtonAsAutocomplete]: open && buttonAsAutocomplete },
            { [classes.buttonAsAutocomplete]: buttonAsAutocomplete },
          )}
          onClick={handleClick}
          sx={error ? {
            color: 'red !important',
            borderColor: 'red !important',
          } : {}}
        >
          <SearchSelectValue
            text={isMobile && iconInMobile ? <LocationIcon fontSize="small" /> : 'Locations'}
            amount={locationFields.filter(locationObj => locationObj.location).length}
            showChip={!(isMobile && iconInMobile)}
          />
        </CustomButtonSearch>
        <Popper
          transition
          placement={isMobile && buttonAsAutocomplete ? 'bottom' : 'bottom-start'}
          id={id}
          open={open}
          anchorEl={anchorEl}
          className={classes.popper}
          disablePortal
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={350}>
              <Paper className={classes.paper}>
                <IconButton
                  aria-label="close"
                  onClick={handleClose}
                  className={classes.closeButton}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
                <form onSubmit={handleSubmit(onSubmit)}>
                  <LocationFilter
                    control={control}
                    fields={locationFields}
                    append={append}
                    remove={remove}
                    errors={formState.errors}
                    trigger={trigger}
                  />
                  <CompactStates compactStates={compactStates} setCompactStates={setCompactStates} />
                  {(showSubmitButton || handleResetFilters) && (
                    <Box display="flex" justifyContent="flex-end" alignItems="center" mt={1}>
                      {handleResetFilters && (
                        <Button variant="outlined" onClick={handleResetFilters} style={{ marginRight: '0.5rem' }}>
                          Reset Filters
                        </Button>
                      )}
                      {showSubmitButton && (
                        <Button
                          variant="contained"
                          type={!noSubmit ? 'submit' : undefined}
                          onClick={noSubmit ? handleClose : undefined}
                          disabled={disabled || disableSubmitButton}
                        >
                          Apply
                        </Button>
                      )}
                    </Box>
                  )}
                </form>
              </Paper>
            </Fade>
          )}
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

LocationsPopper.defaultProps = {
  defaults: '{}',
  defaultsReady: false,
  setQueryStringValue: null,
  showSubmitButton: true,
  buttonAsAutocomplete: false,
  setPreventSearch: () => {},
  handleResetFilters: null,
  iconInMobile: false,
  noSubmit: false,
  error: false,
  setError: null,
};

LocationsPopper.propTypes = {
  defaults: PropTypes.string,
  defaultsReady: PropTypes.bool,
  setQueryStringValue: PropTypes.func,
  showSubmitButton: PropTypes.bool,
  buttonAsAutocomplete: PropTypes.bool,
  setPreventSearch: PropTypes.func,
  handleResetFilters: PropTypes.func,
  iconInMobile: PropTypes.bool,
  noSubmit: PropTypes.bool,
  error: PropTypes.bool,
  setError: PropTypes.func,
};

export default LocationsPopper;
